// "Añadir nueva marca" con la interfaz del onboarding (ADR-2026-10-02-marca-nueva-como-el-onboarding).
//
// Las mismas pantallas del onboarding (link, tablero, lista), en tres pasos y para una marca
// ADICIONAL: el escaneo va con `marca_nueva` (no toca la marca default ni la empresa) y
// confirmar guarda sólo la marca y la deja activa.

import { supabase } from '../supabase';
import { limpiarCacheDeProductos } from '../../pages/v2/productsCache';
import { asegurarSaldoLeido, leerBilleterasDeMarcas, leerSaldo, ultimoSaldoLeido } from '../saldo';
import { cuentaYPermisos } from '../permisosDelEquipo';
import { eliminarMarca } from '../eliminarMarca';
import type { Tramo } from './dialogo';
import { enGuion, MARCA_DEL_GUION } from './guion';
import type { ColorDeMarca, TipografiasDeMarca } from './escaneo';

export const MARCA_NUEVA = {
  link: '/nueva-marca',
  marca: '/nueva-marca/marca',
  lista: '/nueva-marca/lista',
} as const;

export const PASOS_DE_MARCA_NUEVA = [MARCA_NUEVA.link, MARCA_NUEVA.marca, MARCA_NUEVA.lista] as const;

export const NOMBRE_DEL_PASO_DE_MARCA_NUEVA: Record<(typeof PASOS_DE_MARCA_NUEVA)[number], string> = {
  [MARCA_NUEVA.link]: 'Su web',
  [MARCA_NUEVA.marca]: 'Tu nueva marca',
  [MARCA_NUEVA.lista]: 'Lista',
};

/** ¿La pantalla es del flujo de marca nueva? (por la ruta: el link todavía no tiene estado). */
export function enMarcaNueva(pathname: string): boolean {
  return pathname === MARCA_NUEVA.link || pathname.startsWith(`${MARCA_NUEVA.link}/`);
}

/**
 * ¿El flujo se retoma con una marca que ya ocupa su lugar? Al recargar "Tu nueva marca" o
 * "Lista", el análisis ya insertó la fila (o la persona ya la confirmó) y cuenta para el tope:
 * el aviso de "sin lugar" la mandaba a la home con su marca hecha (review Greptile #211).
 * Desde el primer paso siempre se controla: ahí arranca una marca nueva, y un estado viejo de
 * otro intento no la habilita. El candado de verdad es el trigger de la base.
 */
export function retomaUnaMarcaDelFlujo(
  pathname: string,
  estado: { flujo?: string; brandId?: string; escaneoId?: string } | null,
): boolean {
  if (pathname === MARCA_NUEVA.link || !enMarcaNueva(pathname)) return false;
  return estado?.flujo === 'marca_nueva' && Boolean(estado.brandId || estado.escaneoId);
}

/**
 * "Describí tu marca" desde la tarjeta de configuración de la home
 * (ADR-2026-10-19-describir-la-marca-con-el-onboarding-nuevo): las mismas tres pantallas que la
 * marca nueva, pero el escaneo y lo que se confirma van a la marca DEFAULT de la cuenta, que es
 * la que tenía que describir. No crea otra marca ni pide un plan con varias.
 */
export const DESCRIBIR_MARCA = {
  link: '/describir-marca',
  marca: '/describir-marca/marca',
  lista: '/describir-marca/lista',
} as const;

export const PASOS_DE_DESCRIBIR_MARCA = [DESCRIBIR_MARCA.link, DESCRIBIR_MARCA.marca, DESCRIBIR_MARCA.lista] as const;

export const NOMBRE_DEL_PASO_DE_DESCRIBIR_MARCA: Record<(typeof PASOS_DE_DESCRIBIR_MARCA)[number], string> = {
  [DESCRIBIR_MARCA.link]: 'Tu web',
  [DESCRIBIR_MARCA.marca]: 'Tu marca',
  [DESCRIBIR_MARCA.lista]: 'Lista',
};

/** ¿La pantalla es de "Describí tu marca"? (por la ruta, como `enMarcaNueva`). */
export function enDescribirMarca(pathname: string): boolean {
  return pathname === DESCRIBIR_MARCA.link || pathname.startsWith(`${DESCRIBIR_MARCA.link}/`);
}

/** Lo que dice Cumbre en el link de "Describí tu marca". El detalle dice "la leo", como el de
 *  la marca nueva: ahí saltan los stickers. */
export const DIALOGO_DE_DESCRIBIR_MARCA: Tramo[] = [
  { texto: '¡Armemos tu marca!', estilo: 'saludo' },
  { texto: '¿Cuál es la web de tu marca?', estilo: 'pregunta' },
  {
    texto: 'Solo necesito el link de tu web o de tu tienda. Yo la leo y saco tus colores, tu logo y tu forma de comunicar.',
    estilo: 'detalle',
  },
];

/**
 * Lo que dice Cumbre en el link de una marca nueva. El detalle tiene que decir "la leo": ahí
 * saltan los stickers de lo que lee (`STICKERS_DE_MARCA_NUEVA_SALTAN_CON`).
 */
/** Lo que, escrito en el detalle de la marca nueva, arranca la cascada de stickers. */
export const STICKERS_DE_MARCA_NUEVA_SALTAN_CON = 'la leo';

export const DIALOGO_DE_MARCA_NUEVA: Tramo[] = [
  { texto: '¡Sumemos una marca!', estilo: 'saludo' },
  { texto: '¿Cuál es tu nueva marca?', estilo: 'pregunta' },
  {
    texto: 'Solo necesito el link de su web o de su tienda. Yo la leo, y la armo aparte de la que ya tienes.',
    estilo: 'detalle',
  },
];

export interface MarcaNuevaParaConfirmar {
  /** La fila que creó el escaneo; null si el análisis falló y hay que crearla con lo revisado. */
  id: string | null;
  nombre: string;
  rubro: string;
  colores: ColorDeMarca[];
  logoUrl: string | null;
  tipografias?: TipografiasDeMarca;
  analisis?: string;
  /** Con "Otro": cómo describió su rubro. Va a la descripción de ESTA marca
   *  (`user_description`, el "Sobre el negocio" que el agente lee de ella); la empresa no se
   *  toca (review #174). */
  rubroDetalle?: string;
}

/** La descripción de la marca con la del rubro al final (una sola vez). */
export function descripcionConRubro(descripcion: string | null | undefined, rubroDetalle: string | undefined): string | null {
  const base = descripcion?.trim() || '';
  const detalle = rubroDetalle?.trim();
  if (!detalle) return base || null;
  const nota = `Describe su rubro como: ${detalle}.`;
  if (base.includes(nota)) return base;
  return base ? `${base}\n\n${nota}` : nota;
}

/**
 * Confirma una marca ADICIONAL: guarda lo que la persona revisó en ESA fila (o la crea, si el
 * análisis falló) y la deja como la activa, como hace `RevisarMarca`. Nunca toca
 * `company_profiles`: la empresa y su marca de siempre quedan como estaban.
 *
 * `conPago` (empresa con facturación por marca, ADR-2026-10-28-marca-nueva-con-pago): la guarda
 * igual pero EN BORRADOR y sin dejarla activa; recién `activarMarcaNueva`, con el plan ya en su
 * billetera, la vuelve real. Sin `conPago`, las escrituras de siempre, salvo una: al actualizar la
 * fila del escaneo nunca escribe `is_draft = false` (ADR-2026-10-29-marca-activa-exige-plan). Si
 * la bandera no se pudo leer, `conPago` llega en false por la rama de respaldo, y pisar el
 * borrador que creó el backend activaba una marca sin pagar. Si la fila sigue en borrador después
 * del update, es una marca que se paga: no se da por hecha.
 */
export async function confirmarMarcaNueva(p: {
  companyId: string;
  marca: MarcaNuevaParaConfirmar;
  conPago?: boolean;
}): Promise<{ ok: true; brandId: string } | { ok: false; error: string }> {
  if (enGuion()) return { ok: true, brandId: p.marca.id ?? MARCA_DEL_GUION };
  const nombre = p.marca.nombre.trim();
  if (!nombre || !p.marca.rubro) return { ok: false, error: 'Falta el nombre o el rubro de la marca.' };
  const datos = {
    name: nombre,
    rubro: p.marca.rubro,
    colors: p.marca.colores,
    logo_url: p.marca.logoUrl,
  };

  let brandId = p.marca.id;
  if (brandId) {
    // La fila la creó el escaneo y puede traer su descripción: la del rubro se suma al final.
    let descripcion: string | null = null;
    if (p.marca.rubroDetalle?.trim()) {
      const { data, error: errLectura } = await supabase
        .from('brand_profiles')
        .select('user_description')
        .eq('id', brandId)
        .eq('id_company', p.companyId)
        .maybeSingle();
      // Sin poder leer lo que había, escribir pisaría la descripción entera con sólo la nota
      // del rubro (review #174): mejor que reintente.
      if (errLectura) return { ok: false, error: 'No pudimos guardar la marca. Intenta de nuevo.' };
      descripcion = descripcionConRubro(
        (data as { user_description?: string | null } | null)?.user_description,
        p.marca.rubroDetalle,
      );
    }
    // `is_draft` sólo se escribe para dejarla EN borrador, nunca para sacarla: el borrador lo
    // saca `activarMarcaNueva` con el plan pago, y el trigger `marca_sin_plan` lo exige.
    const cambios = {
      ...datos,
      ...(p.conPago ? { is_draft: true } : {}),
      ...(descripcion ? { user_description: descripcion } : {}),
    };
    const { data: filas, error } = await supabase
      .from('brand_profiles')
      .update(cambios)
      .eq('id', brandId)
      .eq('id_company', p.companyId)
      .select('is_draft, is_default');
    if (error && esErrorDeMarcaSinPlan(error)) return { ok: false, error: MENSAJE_MARCA_SIN_PLAN };
    if (error) return { ok: false, error: 'No pudimos guardar la marca. Intenta de nuevo.' };
    const fila = (filas as Array<{ is_draft?: boolean | null; is_default?: boolean | null }> | null)?.[0];
    // Sin `conPago` y todavía en borrador: la empresa factura por marca y la bandera no se pudo
    // leer. Dejarla activa la volvería la marca elegida sin existir; que lo intente de nuevo y
    // pase por el plan. La default (Describí tu marca) no se paga aparte.
    if (!p.conPago && fila?.is_draft === true && fila.is_default !== true) {
      return { ok: false, error: MENSAJE_MARCA_SIN_PLAN };
    }
  } else {
    const descripcion = descripcionConRubro(null, p.marca.rubroDetalle);
    const { data, error } = await supabase
      .from('brand_profiles')
      .insert({
        ...datos,
        is_draft: p.conPago === true,
        id_company: p.companyId,
        is_default: false,
        ...(p.marca.tipografias ? { fonts: p.marca.tipografias } : {}),
        ...(p.marca.analisis ? { context: p.marca.analisis } : {}),
        ...(descripcion ? { user_description: descripcion } : {}),
      })
      .select('id')
      .single();
    // El tope de marcas del plan lo pone la base (ADR-2026-10-27-tope-de-marcas-en-la-base):
    // reintentar no cambia nada, así que se dice qué pasó.
    if (error && String((error as { message?: unknown }).message ?? '').includes('tope_de_marcas')) {
      return { ok: false, error: 'Llegaste al máximo de marcas de tu plan. Para sumar otra, mirá los planes.' };
    }
    // Con facturación por marca, una marca no nace activa sin plan (pasa si `conPago` llegó en
    // false porque no se pudo leer la bandera): reintentar ya la lee y la manda a pagar.
    if (error && esErrorDeMarcaSinPlan(error)) return { ok: false, error: MENSAJE_MARCA_SIN_PLAN };
    if (error || !data) return { ok: false, error: 'No pudimos crear la marca. Intenta de nuevo.' };
    brandId = (data as { id: string }).id;
  }

  // Con pago, queda en borrador hasta que la billetera tenga plan: todavía no es la activa.
  if (p.conPago) return { ok: true, brandId };
  await dejarlaActiva(brandId);
  return { ok: true, brandId };
}

/** La marca recién creada pasa a ser la ACTIVA (ADR-2026-08-04-la-marca-nueva-arranca-vacia). */
async function dejarlaActiva(brandId: string): Promise<void> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) await supabase.from('profiles').update({ current_brand_id: brandId }).eq('id', user.id);
  } catch {
    /* best-effort: la marca ya quedó guardada; se puede elegir desde el selector */
  }
  // El catálogo cacheado es de la marca anterior: sin esto la home lo mostraría un instante.
  limpiarCacheDeProductos();
  window.dispatchEvent(new CustomEvent('atlas:brand-changed', { detail: { brandId } }));
}

// ── Marca nueva con pago (ADR-2026-10-28-marca-nueva-con-pago) ──────────────────────────────
// Con `company_profiles.facturacion_por_marca` cada marca paga su plan Ultra: sólo el dueño suma
// marcas, y la nueva queda en borrador (`is_draft = true`, invisible para la app) hasta que el
// webhook de pago le deja la billetera con plan. Sin la bandera nada de esto corre.

export const MOTIVO_SOLO_ADMIN = 'Sólo el admin puede sumar marcas';

/**
 * Lo que se lee cuando la base frena una marca que se activa sin plan (trigger
 * `brand_profiles_marca_activa_exige_plan`, ADR-2026-10-29-marca-activa-exige-plan).
 */
export const MENSAJE_MARCA_SIN_PLAN = 'Esta marca se activa cuando pagás su plan.';

/** ¿El error es el del trigger de facturación por marca? (P0001 con `marca_sin_plan` en el mensaje). */
export function esErrorDeMarcaSinPlan(error: unknown): boolean {
  return String((error as { message?: unknown } | null)?.message ?? '').includes('marca_sin_plan');
}

/**
 * `seguir` = esta persona puede empezar una marca; si entra en el plan lo dice después
 * `leerLugarParaMarcas` (el tope del plan, que con la bandera no aplica:
 * ADR-2026-10-28-facturacion-por-marca-sin-tope-de-marcas).
 */
export type DecisionDeMarcaNueva = 'seguir' | 'solo_admin';

/**
 * ¿Puede empezar una marca nueva? Con la bandera decide quién es (el plan lo paga la marca
 * nueva y lo paga el dueño); sin ella, cualquiera sigue y decide el lugar del plan.
 */
export function decisionDeMarcaNueva(e: { facturaPorMarca: boolean; esDueno: boolean }): DecisionDeMarcaNueva {
  if (e.facturaPorMarca) return e.esDueno ? 'seguir' : 'solo_admin';
  return 'seguir';
}

/**
 * La primera decisión de los botones de "Añadir nueva marca"; la segunda es el lugar del plan
 * (`leerLugarParaMarcas`). La bandera sale del último saldo leído (el shell lo lee al montar);
 * sin bandera no consulta nada más. Si el saldo no se pudo leer se sigue como sin bandera: al
 * confirmar, `TableroDeMarca` lo vuelve a mirar con una lectura propia antes de escribir nada.
 */
export async function decidirMarcaNueva(): Promise<DecisionDeMarcaNueva> {
  const saldo = await asegurarSaldoLeido();
  if (!saldo?.facturaPorMarca) return 'seguir';
  const esDueno = await cuentaYPermisos()
    .then((c) => c.esDueno)
    .catch(() => false);
  return decisionDeMarcaNueva({ facturaPorMarca: true, esDueno });
}

/**
 * Si la empresa factura por marca: lectura propia; si falla, el último saldo leído; si tampoco
 * hay, sin bandera (las ramas de hoy: ADR-2026-10-28-marca-nueva-con-pago-2).
 */
async function facturaPorMarcaDe(companyId: string): Promise<boolean> {
  try {
    return (await leerSaldo(companyId)).facturaPorMarca;
  } catch {
    return ultimoSaldoLeido()?.facturaPorMarca === true;
  }
}

/**
 * Lo que decide confirmar: si la empresa factura por marca (`facturaPorMarcaDe`) y si quien
 * confirma es el dueño. `null` sólo si, con bandera, no se pudo saber quién es: no se escribe
 * nada a ciegas.
 */
export async function leerCondicionesDeMarcaNueva(
  companyId: string,
): Promise<{ facturaPorMarca: boolean; esDueno: boolean } | null> {
  const facturaPorMarca = await facturaPorMarcaDe(companyId);
  if (!facturaPorMarca) return { facturaPorMarca: false, esDueno: true };
  try {
    const { esDueno } = await cuentaYPermisos();
    return { facturaPorMarca: true, esDueno };
  } catch {
    return null;
  }
}

/** ¿La billetera de la marca ya tiene plan? (el alta la aplica sólo el webhook de pago). */
export async function marcaYaTienePlan(brandId: string): Promise<boolean> {
  const billeteras = await leerBilleterasDeMarcas([brandId]);
  return billeteras.get(brandId)?.plan != null;
}

/**
 * El pago de la marca nueva entró (su billetera ya tiene plan): deja de ser borrador y pasa a
 * ser la activa, con las mismas escrituras que la confirmación sin pago.
 */
export async function activarMarcaNueva(p: {
  companyId: string;
  brandId: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  if (enGuion()) return { ok: true };
  const { error } = await supabase
    .from('brand_profiles')
    .update({ is_draft: false })
    .eq('id', p.brandId)
    .eq('id_company', p.companyId);
  // El pago todavía no llegó a la billetera: la base no la deja activarse sin plan.
  if (error && esErrorDeMarcaSinPlan(error)) return { ok: false, error: MENSAJE_MARCA_SIN_PLAN };
  if (error) return { ok: false, error: 'No pudimos activar la marca. Intenta de nuevo.' };
  await dejarlaActiva(p.brandId);
  return { ok: true };
}

/** La marca principal (default, no borrador) de la empresa: a donde van los productos de una marca que se borra. */
async function marcaPrincipalDe(companyId: string): Promise<{ id: string } | undefined> {
  const { data, error } = await supabase
    .from('brand_profiles')
    .select('id')
    .eq('id_company', companyId)
    .eq('is_default', true)
    .eq('is_draft', false)
    .limit(1);
  if (error) throw error;
  return (data as Array<{ id: string }> | null)?.[0];
}

/**
 * "Cancelar" en el paso del plan: borra el borrador por el mismo camino que Configuración →
 * Marcas (`eliminarMarca`: los productos a la principal, después la fila). Nunca borra una marca
 * que ya no es borrador ni una que ya tiene plan (el pago entró tarde): ésa se activa
 * (`activada: true`) en vez de perder lo pagado.
 */
export async function cancelarMarcaNueva(p: {
  companyId: string;
  brandId: string;
}): Promise<{ ok: true; activada: boolean } | { ok: false; error: string }> {
  if (enGuion()) return { ok: true, activada: false };
  try {
    const { data: fila, error: errFila } = await supabase
      .from('brand_profiles')
      .select('is_draft')
      .eq('id', p.brandId)
      .eq('id_company', p.companyId)
      .maybeSingle();
    if (errFila) throw errFila;
    // Ya no existe o ya es una marca de verdad: no hay borrador que borrar.
    if (!fila || (fila as { is_draft?: boolean }).is_draft !== true) return { ok: true, activada: false };

    if (await marcaYaTienePlan(p.brandId)) {
      const r = await activarMarcaNueva(p);
      return r.ok ? { ok: true, activada: true } : r;
    }

    const principal = await marcaPrincipalDe(p.companyId);
    if (!principal) return { ok: false, error: 'No pudimos descartar la marca. Intenta de nuevo.' };
    await eliminarMarca(p.brandId, principal.id);
    return { ok: true, activada: false };
  } catch {
    return { ok: false, error: 'No pudimos descartar la marca. Intenta de nuevo.' };
  }
}

/**
 * Cuánto tiene que llevar un borrador de marca adicional SIN billetera y sin actividad para darlo
 * por abandonado. El escaneo de una marca nueva la crea en borrador; si el dueño se va sin pasar
 * por "Cancelar" (atrás del navegador, otra sección, cerrar la pestaña) queda ahí, invisible. La
 * actividad es `updated_at`, que la pantalla de la marca nueva refresca cada
 * `LATIDO_DEL_BORRADOR_MS` mientras está abierta (`marcarBorradorEnUso`): una pestaña que sigue
 * en el alta no pierde su borrador por arrancar otra marca en otra pestaña
 * (ADR-2026-10-28-borradores-de-marca-abandonados, ADR-2026-10-28-borradores-de-marca-abandonados-2).
 */
export const BORRADOR_ABANDONADO_MS = 60 * 60 * 1000;

/** Cada cuánto la pantalla de una marca nueva marca su borrador como en uso. */
export const LATIDO_DEL_BORRADOR_MS = 10 * 60 * 1000;

/**
 * El latido: deja `updated_at = ahora` en el borrador mientras su pantalla está abierta. Sólo
 * toca borradores (una marca ya activa no se toca). Nunca tira: un latido perdido lo cubre el
 * siguiente, y el margen es seis latidos.
 */
export async function marcarBorradorEnUso(companyId: string, brandId: string): Promise<void> {
  if (enGuion()) return;
  try {
    await supabase
      .from('brand_profiles')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', brandId)
      .eq('id_company', companyId)
      .eq('is_draft', true);
  } catch {
    // El próximo latido lo intenta de nuevo.
  }
}

type BorradorConFechas = BorradorDeMarca & { created_at: string | null; updated_at?: string | null };

/**
 * Qué borradores son abandonados: adicionales (nunca la default), sin billetera (nadie pagó ni
 * hay un cobro que la haya tocado) y sin actividad —la más nueva entre creado y último latido—
 * hace más de `BORRADOR_ABANDONADO_MS`. Sin ninguna fecha legible no se da por abandonado. Pura.
 */
export function borradoresAbandonados(e: {
  borradores: BorradorConFechas[];
  conBilletera: Set<string>;
  ahora: number;
}): string[] {
  return e.borradores
    .filter((b) => {
      if (b.is_default || e.conBilletera.has(b.id)) return false;
      const fechas = [b.created_at, b.updated_at]
        .map((f) => (f ? Date.parse(f) : NaN))
        .filter((t) => Number.isFinite(t));
      if (fechas.length === 0) return false;
      return e.ahora - Math.max(...fechas) > BORRADOR_ABANDONADO_MS;
    })
    .map((b) => b.id);
}

/**
 * Al arrancar una marca nueva con facturación por marca: borra los borradores abandonados de
 * intentos anteriores por el mismo camino que "Cancelar" (`eliminarMarca`). Sin bandera no hace
 * nada (ahí el borrador es el del guard de siempre). Nunca tira ni frena el alta: lo que no se
 * pudo borrar queda para la próxima.
 */
export async function descartarBorradoresAbandonados(companyId: string): Promise<void> {
  if (enGuion()) return;
  try {
    if (!(await facturaPorMarcaDe(companyId))) return;
    const { data, error } = await supabase
      .from('brand_profiles')
      .select('id, is_default, created_at, updated_at')
      .eq('id_company', companyId)
      .eq('is_draft', true);
    if (error) throw error;
    const borradores = (data ?? []) as BorradorConFechas[];
    const adicionales = borradores.filter((b) => !b.is_default).map((b) => b.id);
    if (adicionales.length === 0) return;
    const billeteras = await leerBilleterasDeMarcas(adicionales);
    const abandonados = borradoresAbandonados({
      borradores,
      conBilletera: new Set(billeteras.keys()),
      ahora: Date.now(),
    });
    if (abandonados.length === 0) return;
    const principal = await marcaPrincipalDe(companyId);
    if (!principal) return;
    for (const id of abandonados) {
      await eliminarMarca(id, principal.id).catch(() => {});
    }
  } catch {
    // Best-effort: un borrador que quedó no traba a nadie.
  }
}

export interface BorradorDeMarca {
  id: string;
  is_default: boolean | null;
}

/**
 * El guard de borradores del shell (`useShellGuards`): qué borrador manda a
 * `/onboarding/revisar-marca/:id` y cuáles ya están pagados y hay que activar.
 *
 * Sin bandera: el más nuevo, como siempre. Con bandera, una marca ADICIONAL en borrador es una
 * marca nueva esperando su pago: sin plan no atrapa a nadie (si no, el dueño que no pagó quedaba
 * encerrado en revisar-marca, que además la volvería real sin plan); con plan, el pago entró
 * después de cerrar el flujo y se activa. El borrador de la marca DEFAULT es un alta a medias y
 * sigue atrapando. `borradores` viene del más nuevo al más viejo.
 */
export function destinoDeLosBorradores(e: {
  facturaPorMarca: boolean;
  borradores: BorradorDeMarca[];
  billeteras: Map<string, { plan: string | null }>;
}): { revisar: string | null; activar: string[] } {
  if (!e.facturaPorMarca) return { revisar: e.borradores[0]?.id ?? null, activar: [] };
  const activar: string[] = [];
  let revisar: string | null = null;
  for (const b of e.borradores) {
    if (b.is_default) {
      revisar ??= b.id;
      continue;
    }
    if (e.billeteras.get(b.id)?.plan != null) activar.push(b.id);
  }
  return { revisar, activar };
}

/**
 * Con un borrador ya encontrado por el guard, a cuál mandar (o `null`). Sin bandera (o si no se
 * puede saber y el último saldo leído no la tiene) devuelve ese mismo, sin otra consulta a las
 * marcas: el guard de hoy. Con bandera activa los ya pagados sin cambiar la marca activa de
 * nadie, y avisa `atlas:brand-changed` SIN `brandId` para que las listas de marcas se recarguen
 * (con `brandId`, quien lo lee lo tomaría por la nueva activa). Tira si no puede leer las marcas:
 * el guard, ante un error, no mueve a nadie.
 */
export async function borradorQueAtrapa(idCompany: string, draftId: string): Promise<string | null> {
  if (!(await facturaPorMarcaDe(idCompany))) return draftId;
  const { data, error } = await supabase
    .from('brand_profiles')
    .select('id, is_default')
    .eq('id_company', idCompany)
    .eq('is_draft', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  const borradores = (data ?? []) as BorradorDeMarca[];
  const adicionales = borradores.filter((b) => !b.is_default).map((b) => b.id);
  const billeteras = await leerBilleterasDeMarcas(adicionales);
  const { revisar, activar } = destinoDeLosBorradores({ facturaPorMarca: true, borradores, billeteras });
  let activadas = 0;
  for (const id of activar) {
    const { error: errActivar } = await supabase
      .from('brand_profiles')
      .update({ is_draft: false })
      .eq('id', id)
      .eq('id_company', idCompany);
    if (!errActivar) activadas++;
  }
  if (activadas > 0) window.dispatchEvent(new CustomEvent('atlas:brand-changed'));
  return revisar;
}
